import { defineComponent } from 'vue';
import { QBtn, QBanner, QSpinner, QCheckbox, QInput, QSelect, QIcon } from 'quasar';
import { AgGridVue } from 'ag-grid-vue3';
import utils from '../../../../../Shared/utils.jsx';
import formatApiError from './formatApiError.js';
import { SENTENCE_CASE_BUTTON_CLASS } from '../../../../../Shared/buttonAppearance.js';
import '../../../css/BasicButton.css';
import { resolveLogicalUri } from '../debugger/flowV2WebUrl.js';
import { describeHttpBinding, formatHttpBindingHint, paramBindingLabel } from '../debugger/flowV2HttpBinding.js';
import {
    PARAM_TYPE_VALUES,
    RETURN_TYPE_VALUES,
    JSON_EMPTY_OBJECT,
    FLOW_JSON_DEFAULT_OPTIONS,
    createParameterRow,
    hydrateParameterRows,
    stripParameterRows,
    applyDragOrder,
    methodNameError,
    methodNameApiNote as apiNameNote,
    METHOD_NAME_HINT,
    METHOD_NAME_API_DETAIL,
    formatTypeLabel,
    parameterNameError,
    parameterDefaultError,
    parameterRowsError,
    jsonParseRewrite,
    formatParameterDefault,
    keepJsonDefault,
} from './methodSignatureParams.js';
import { editCellAfterGridFlush, suppressTabToColumn } from './gridEditNavigation.js';
import './methodSignaturePanel.css';

const BTN = `${SENTENCE_CASE_BUTTON_CLASS} q-my-xs`;
const ROW_SELECTION = { mode: 'multiRow', checkboxes: true, headerCheckbox: true };

function descriptionPreview(value) {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    if (text.length <= 80)
        return text;
    return `${text.slice(0, 80)}…`;
}

function typeEditorValues(current, baseValues) {
    const values = [...baseValues];
    if (current && !values.includes(current))
        values.unshift(current);
    return values;
}

export default defineComponent({
    name: 'flow-v2-method-signature-panel',
    components: { QBtn, QBanner, QSpinner, QCheckbox, QInput, QSelect, QIcon, AgGridVue },
    props: {
        flowId: { type: String, required: true },
        flowType: { type: String, required: true },
        root: { type: Object, default: null },
        scopeitems: { type: Object, default: null },
        controlscope: { type: Object, default: null },
        channelConfig: { type: Object, default: null },
    },
    emits: ['saved'],
    data() {
        return {
            loading: true,
            saving: false,
            error: null,
            model: null,
            parameterRows: [],
            selectedRows: [],
            gridApi: null,
            logicalUrl: '',
            defaultRewrite: null,
        };
    },
    computed: {
        methodNameMessage() {
            if (!this.model)
                return null;
            return methodNameError(this.model.Name);
        },
        methodNameApiNote() {
            if (!this.model || this.methodNameMessage)
                return null;
            return apiNameNote(this.model.Name);
        },
        returnTypeOptions() {
            const current = this.model?.ReturnType;
            const showStoredType = this.flowType === 'APIHeader';
            return typeEditorValues(current, RETURN_TYPE_VALUES).map((value) => ({
                label: formatTypeLabel(value, showStoredType),
                value,
            }));
        },
        isEligibleApi() {
            if (this.flowType !== 'APIHeader')
                return false;
            if (this.channelConfig && this.channelConfig.HasMedia)
                return false;
            return true;
        },
        httpBinding() {
            const params = this.model?.Parameters || [];
            return describeHttpBinding(this.model?.Name, params);
        },
        httpBindingHint() {
            return formatHttpBindingHint(this.httpBinding);
        },
        namedParameters() {
            return (this.model?.Parameters || []).filter((p) => p && String(p.Name || '').trim());
        },
        isDeleteDisabled() {
            return this.selectedRows.length < 1;
        },
        columnDefs() {
            return [
                {
                    colId: 'drag',
                    headerName: '',
                    rowDrag: true,
                    width: 42,
                    maxWidth: 42,
                    minWidth: 42,
                    sortable: false,
                    resizable: false,
                    suppressHeaderMenuButton: true,
                    suppressMovable: true,
                },
                {
                    field: 'Name',
                    headerName: 'Name',
                    headerTooltip: 'Letter or underscore, then letters, digits, or underscores',
                    editable: true,
                    flex: 1,
                    minWidth: 120,
                    sortable: false,
                    cellDataType: false,
                    valueSetter: (p) => {
                        const message = parameterNameError(p.newValue, this.parameterRows, p.data);
                        if (message) {
                            this.error = message;
                            return false;
                        }
                        p.data.Name = String(p.newValue ?? '').trim();
                        this.error = parameterDefaultError(p.data, this.jsonDefaultOptions());
                        this.defaultRewrite = null;
                        return true;
                    },
                    cellStyle: (p) => {
                        if (parameterNameError(p.value, this.parameterRows, p.data))
                            return { background: '#ffebee', color: '#b71c1c' };
                        return null;
                    },
                    suppressKeyboardEvent: (params) => suppressTabToColumn(params, 'Type', 'Enter'),
                },
                {
                    field: 'Type',
                    headerName: 'Type',
                    editable: true,
                    width: 220,
                    minWidth: 160,
                    sortable: false,
                    cellDataType: false,
                    valueFormatter: (p) => formatTypeLabel(p.value, this.flowType === 'APIHeader'),
                    cellEditor: 'agSelectCellEditor',
                    cellEditorParams: (params) => ({
                        values: typeEditorValues(params.data?.Type, PARAM_TYPE_VALUES),
                    }),
                    suppressKeyboardEvent: (params) => suppressTabToColumn(params, 'Description'),
                },
                {
                    field: 'Description',
                    headerName: 'Description',
                    editable: true,
                    flex: 1.4,
                    minWidth: 160,
                    sortable: false,
                    cellDataType: false,
                    cellEditor: 'agLargeTextCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        maxLength: 20000,
                        rows: 6,
                        cols: 48,
                    },
                    suppressKeyboardEvent: (params) => {
                        if (!params.editing)
                            return false;
                        return params.event?.key === 'Enter' && !params.event.ctrlKey && !params.event.metaKey;
                    },
                    cellRenderer: (params) => {
                        const full = params.value == null ? '' : String(params.value);
                        const span = document.createElement('span');
                        span.textContent = descriptionPreview(full);
                        if (full)
                            span.title = full;
                        return span;
                    },
                },
                {
                    field: 'Out',
                    headerName: 'Out',
                    headerTooltip: 'Send this value back to the calling flow.',
                    editable: true,
                    width: 88,
                    sortable: false,
                    cellDataType: 'boolean',
                },
                {
                    field: 'DefaultValue',
                    headerName: 'Default value',
                    headerTooltip: 'Used when the caller does not supply a value. Non-string inputs need a value of that type. A flowchart object is an empty object, an empty list, or nothing. An API object is code such as new { }. Out parameters need a variable name.',
                    editable: true,
                    flex: 1,
                    minWidth: 140,
                    sortable: false,
                    cellDataType: false,
                    valueFormatter: (p) => formatParameterDefault(p.value, {
                        api: this.flowType === 'APIHeader',
                        // The choice list formats each option with no row. Those options are flowchart object defaults.
                        flowJson: p.data ? this.usesFlowJsonChoices(p.data) : this.flowType !== 'APIHeader',
                    }),
                    cellEditorSelector: (p) => {
                        if (!this.usesFlowJsonChoices(p.data))
                            return undefined;
                        const current = p.data?.DefaultValue == null ? '' : String(p.data.DefaultValue);
                        const values = FLOW_JSON_DEFAULT_OPTIONS.map((item) => item.value);
                        if (current && !values.includes(current))
                            values.unshift(current);
                        return {
                            component: 'agSelectCellEditor',
                            params: { values },
                        };
                    },
                    valueSetter: (p) => {
                        const raw = p.newValue == null ? '' : String(p.newValue);
                        const message = parameterDefaultError({ ...p.data, DefaultValue: raw }, this.jsonDefaultOptions());
                        if (message) {
                            this.error = message;
                            this.noteJsonRewrite(p.data, raw);
                            return false;
                        }
                        this.defaultRewrite = null;
                        const type = String(p.data?.Type ?? '').trim();
                        const isStringInput = !p.data?.Out && (!type || type === 'string');
                        p.data.DefaultValue = isStringInput ? raw : raw.trim();
                        this.error = parameterNameError(p.data.Name, this.parameterRows, p.data);
                        return true;
                    },
                },
            ];
        },
        defaultColDef() {
            return {
                resizable: true,
                suppressMovable: true,
            };
        },
    },
    watch: {
        flowId: { immediate: true, handler() { this.load(); } },
        flowType() { this.load(); },
        channelConfig() { this.refreshLogicalUrl(); },
        'model.Name'() {
            if (this.model)
                this.refreshLogicalUrl();
        },
    },
    methods: {
        jsonDefaultOptions() {
            return { api: this.flowType === 'APIHeader' };
        },
        usesFlowJsonChoices(row) {
            if (!row || row.Out || this.flowType === 'APIHeader')
                return false;
            return String(row.Type ?? '').trim() === 'json';
        },
        noteJsonRewrite(row, raw) {
            if (this.flowType !== 'APIHeader' || !row?._rowId)
                this.defaultRewrite = null;
            else {
                const expression = jsonParseRewrite(raw);
                this.defaultRewrite = expression ? { rowId: row._rowId, expression } : null;
            }
        },
        applyDefaultRewrite() {
            const offer = this.defaultRewrite;
            if (!offer)
                return;
            const row = this.parameterRows.find((item) => item._rowId === offer.rowId);
            if (!row)
                return;
            row.DefaultValue = offer.expression;
            this.defaultRewrite = null;
            this.error = parameterNameError(row.Name, this.parameterRows, row)
                || parameterDefaultError(row, this.jsonDefaultOptions());
            this.syncParameters();
            this.gridApi?.refreshCells?.({ force: true });
        },
        syncParameters() {
            if (!this.model)
                return;
            this.model.Parameters = stripParameterRows(this.parameterRows);
        },
        async refreshLogicalUrl() {
            if (!this.model)
                return;
            const url = await resolveLogicalUri(utils.api, {
                projectPath: this.model.Path || '',
                projectName: '',
                className: '',
                methodName: this.model.Name || '',
            });
            this.logicalUrl = url;
            if (this.model.Configuration)
                this.model.Configuration.URITemplate = url;
        },
        onExposeChanged(value) {
            if (!this.model?.Configuration)
                return;
            this.model.Configuration.WebAccessible = !!value;
            if (this.model.Configuration.WebAccessible)
                this.refreshLogicalUrl();
        },
        async load() {
            if (!this.flowId || !this.flowType) return;
            this.loading = true;
            this.error = null;
            try {
                const model = await utils.api.get(
                    `Apps/FlowV2/GetMethodHeader?Type=${this.flowType}&ID=${this.flowId}`,
                );
                if (!model.Configuration) {
                    model.Configuration = {
                        Method: 'GET',
                        URITemplate: '',
                        Public: false,
                        WebAccessible: false,
                    };
                }
                if (!Array.isArray(model.Parameters))
                    model.Parameters = [];
                this.model = model;
                this.parameterRows = hydrateParameterRows(model.Parameters);
                this.selectedRows = [];
                await this.refreshLogicalUrl();
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.loading = false;
            }
        },
        onGridReady(params) {
            this.gridApi = params.api;
        },
        getRowId(params) {
            return params.data?._rowId;
        },
        onSelectionChanged() {
            this.selectedRows = this.gridApi?.getSelectedRows?.() || [];
        },
        onCellValueChanged(event) {
            const field = event?.colDef?.field;
            const row = event?.data;
            if (field === 'Type' && row && !row.Out && String(row.Type ?? '').trim() === 'json') {
                if (!keepJsonDefault(row.DefaultValue, this.flowType === 'APIHeader'))
                    row.DefaultValue = JSON_EMPTY_OBJECT;
            }
            this.syncParameters();
            if (field === 'Name' || field === 'Type' || field === 'Out' || field === 'DefaultValue') {
                this.error = parameterNameError(row?.Name, this.parameterRows, row)
                    || parameterDefaultError(row, this.jsonDefaultOptions());
                if (field !== 'DefaultValue')
                    this.defaultRewrite = null;
                if (field === 'Type')
                    this.gridApi?.refreshCells?.({ force: true });
            }
        },
        onRowDragEnd() {
            const orderedIds = [];
            this.gridApi?.forEachNode?.((node) => {
                if (node?.data?._rowId)
                    orderedIds.push(node.data._rowId);
            });
            this.parameterRows = applyDragOrder(this.parameterRows, orderedIds);
            this.syncParameters();
        },
        addRow() {
            const row = createParameterRow();
            this.parameterRows = [...this.parameterRows, row];
            this.error = parameterNameError(row.Name, this.parameterRows, row);
            this.syncParameters();
            this.$nextTick(() => {
                editCellAfterGridFlush(this.gridApi, row._rowId, 'Name');
            });
        },
        removeSelected() {
            const selected = this.gridApi?.getSelectedRows?.() || [];
            if (!selected.length)
                return;
            const removeIds = new Set(selected.map((row) => row._rowId));
            this.parameterRows = this.parameterRows.filter((row) => !removeIds.has(row._rowId));
            this.selectedRows = [];
            this.syncParameters();
            this.$nextTick(() => {
                this.gridApi?.setGridOption?.('rowData', this.parameterRows);
            });
        },
        async save() {
            if (!this.model || this.saving)
                return;
            this.gridApi?.stopEditing?.();
            const nameError = methodNameError(this.model.Name);
            if (nameError) {
                this.error = nameError;
                return;
            }
            const paramError = parameterRowsError(this.parameterRows, this.jsonDefaultOptions());
            if (paramError) {
                this.error = paramError;
                this.defaultRewrite = null;
                if (this.flowType === 'APIHeader') {
                    for (const row of this.parameterRows) {
                        if (parameterDefaultError(row, this.jsonDefaultOptions()) !== paramError)
                            continue;
                        const expression = jsonParseRewrite(row.DefaultValue);
                        if (expression)
                            this.defaultRewrite = { rowId: row._rowId, expression };
                        break;
                    }
                }
                return;
            }
            this.defaultRewrite = null;
            this.model.Name = String(this.model.Name).trim();
            for (const row of this.parameterRows) {
                row.Name = String(row.Name ?? '').trim();
                const type = String(row.Type ?? '').trim();
                if (row.Out || (type && type !== 'string'))
                    row.DefaultValue = String(row.DefaultValue ?? '').trim();
            }
            this.syncParameters();
            this.saving = true;
            this.error = null;
            try {
                if (this.model.Configuration) {
                    this.model.Configuration.URITemplate = this.logicalUrl || this.model.Configuration.URITemplate;
                    this.model.Configuration.Method = this.httpBinding.verb;
                    if (!this.isEligibleApi)
                        this.model.Configuration.WebAccessible = false;
                }
                const data = this.model;
                const res = await utils.api.request(
                    'POST',
                    `Apps/FlowV2/SaveMethodHeader?Type=${data.Type || this.flowType}`,
                    data,
                );
                if (!res.Success)
                    this.error = res.Reason || 'Save failed';
                else
                    this.$emit('saved', data);
            }
            catch (err) {
                this.error = formatApiError(err);
            }
            finally {
                this.saving = false;
            }
        },
    },
    render() {
        if (this.loading) {
            return (
                <div class="q-pa-lg flex flex-center">
                    <QSpinner color="primary" size="40px" />
                </div>
            );
        }
        if (this.error && !this.model) {
            return <QBanner class="bg-red-1 text-negative">{this.error}</QBanner>;
        }
        return (
            <div class="flow-v2-signature">
                {this.error ? (
                    <QBanner
                        class="bg-red-1 text-negative q-mb-sm"
                        v-slots={this.defaultRewrite ? {
                            action: () => (
                                <QBtn
                                    flat
                                    dense
                                    no-caps
                                    color="negative"
                                    label="Wrap with json.Parse"
                                    onClick={() => this.applyDefaultRewrite()}
                                />
                            ),
                        } : undefined}
                    >
                        {this.error}
                    </QBanner>
                ) : null}
                {this.model ? (
                    <div class="flow-v2-signature__header">
                        <div class="flow-v2-signature__identity">
                            <QInput
                                class={{
                                    'flow-v2-signature__name': true,
                                    'flow-v2-signature__name--api-note': !!this.methodNameApiNote,
                                }}
                                dense
                                outlined
                                label="Name"
                                hint={this.methodNameApiNote || METHOD_NAME_HINT}
                                modelValue={this.model.Name || ''}
                                error={!!this.methodNameMessage}
                                errorMessage={this.methodNameMessage || undefined}
                                onUpdate:modelValue={(v) => {
                                    this.model.Name = v;
                                    this.error = null;
                                }}
                                v-slots={{
                                    append: () => utils.tt(
                                        <span class="flow-v2-signature__name-tip">{METHOD_NAME_API_DETAIL}</span>,
                                        <QIcon name="mdi-information-outline" size="16px" class="cursor-pointer text-grey-7" />,
                                    ),
                                }}
                            />
                            <QSelect
                                class="flow-v2-signature__return"
                                dense
                                outlined
                                optionsDense
                                emitValue
                                mapOptions
                                label="Return type"
                                hint="Value sent back to the caller. Choose Nothing when there is no return value."
                                options={this.returnTypeOptions}
                                modelValue={this.model.ReturnType ?? ''}
                                onUpdate:modelValue={(v) => { this.model.ReturnType = v; }}
                            />
                        </div>
                        <QInput
                            class="flow-v2-signature__description"
                            dense
                            outlined
                            autogrow
                            type="textarea"
                            label="Description"
                            hint="Shown when someone adds this method to a flow."
                            modelValue={this.model.Description || ''}
                            onUpdate:modelValue={(v) => { this.model.Description = v; }}
                        />
                    </div>
                ) : null}
                <div class="flow-v2-signature__toolbar">
                    <div class="text-subtitle2">Parameters</div>
                    {utils.tt('Add parameter', (
                        <QBtn
                            flat
                            round
                            dense
                            icon="mdi-plus"
                            size="sm"
                            disable={this.saving}
                            onClick={() => this.addRow()}
                        />
                    ))}
                    {utils.tt('Delete selected', (
                        <QBtn
                            flat
                            round
                            dense
                            icon="mdi-trash-can-outline"
                            size="sm"
                            disable={this.isDeleteDisabled || this.saving}
                            onClick={() => this.removeSelected()}
                        />
                    ))}
                </div>
                <div class="flow-v2-signature__grid ag-theme-alpine">
                    <AgGridVue
                        style={{ width: '100%', height: '100%' }}
                        class="ag-theme-alpine"
                        columnDefs={this.columnDefs}
                        defaultColDef={this.defaultColDef}
                        rowData={this.parameterRows}
                        getRowId={this.getRowId}
                        headerHeight={32}
                        rowHeight={36}
                        rowSelection={ROW_SELECTION}
                        rowDragManaged={true}
                        animateRows={true}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                        overlayNoRowsTemplate="No parameters yet"
                        onGridReady={this.onGridReady}
                        onSelectionChanged={this.onSelectionChanged}
                        onCellValueChanged={this.onCellValueChanged}
                        onRowDragEnd={this.onRowDragEnd}
                    />
                </div>
                <div class="flow-v2-signature__web">
                    <div class="flow-v2-signature__actions">
                        {this.isEligibleApi && this.model ? (
                            <QCheckbox
                                class="flow-v2-web-access-check caption sform-checkbox-neutral"
                                dense
                                label="Expose as web API"
                                modelValue={!!this.model.Configuration?.WebAccessible}
                                onUpdate:modelValue={(v) => this.onExposeChanged(v)}
                            />
                        ) : null}
                        <QBtn
                            color="primary"
                            unelevated
                            no-caps
                            class={BTN}
                            label="Save"
                            loading={this.saving}
                            onClick={() => this.save()}
                        />
                    </div>
                    {this.isEligibleApi && this.model?.Configuration?.WebAccessible ? (
                        <div>
                            <QInput
                                class="q-mt-sm"
                                dense
                                outlined
                                readonly
                                label="Web URL"
                                prefix={this.httpBinding.verb}
                                modelValue={this.logicalUrl}
                                hint="Pin a draft or release with header X-CC-Flow-Release (sandbox or the version label) or query _release=."
                            />
                            <div class="flow-v2-http-binding text-caption text-grey-7">
                                {this.httpBindingHint}
                                {this.namedParameters.length ? (
                                    <ul class="flow-v2-http-binding__list">
                                        {this.namedParameters.map((p) => (
                                            <li key={`${p.Name}:${p.Type}:${p.Out}`} class="flow-v2-http-binding__row">
                                                <span class="flow-v2-http-binding__name">{p.Name}</span>
                                                <span class="flow-v2-http-binding__kind">{paramBindingLabel(p, this.httpBinding.verb)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    },
});
